import { Pencil, Trash2 } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { ResourceTagRecord } from '@/types/resource-tag'

interface ResourceTagRecordsTableProps {
  records: ResourceTagRecord[]
  editable: boolean
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  onToggleSelectAll: (checked: boolean) => void
  onEdit: (record: ResourceTagRecord) => void
  onDelete: (record: ResourceTagRecord) => void
}

export function ResourceTagRecordsTable({
  records,
  editable,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onEdit,
  onDelete,
}: ResourceTagRecordsTableProps) {
  const allSelected = records.length > 0 && records.every((r) => selectedIds.has(r.id))

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                disabled={!editable || records.length === 0}
                onCheckedChange={(v) => onToggleSelectAll(v === true)}
                aria-label="Select all records"
              />
            </TableHead>
            <TableHead>Resource attribute</TableHead>
            <TableHead>Resource value</TableHead>
            <TableHead className="w-24 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                No records yet
              </TableCell>
            </TableRow>
          ) : (
            records.map((record) => (
              <TableRow key={record.id}>
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(record.id)}
                    disabled={!editable}
                    onCheckedChange={() => onToggleSelect(record.id)}
                    aria-label={`Select ${record.resourceAttribute}=${record.resourceValue}`}
                  />
                </TableCell>
                <TableCell className="font-mono text-sm">{record.resourceAttribute}</TableCell>
                <TableCell>{record.resourceValue}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      disabled={!editable}
                      onClick={() => onEdit(record)}
                      aria-label="Edit record"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      disabled={!editable}
                      onClick={() => onDelete(record)}
                      aria-label="Delete record"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
